/**
 * Booking Types - Single Source of Truth
 * Mirrors backend: backend-tms/src/carrier-rates/entities/booking.entity.ts
 * Rule 15: No duplication - shared across backend and frontend
 */

/**
 * BookingStatus enum - matches backend exactly
 * @see backend-tms/src/carrier-rates/entities/booking.entity.ts
 */
export enum BookingStatus {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  SCHEDULED = 'SCHEDULED',
  IN_TRANSIT = 'IN_TRANSIT',
  DELIVERED = 'DELIVERED',
  CANCELLED = 'CANCELLED',
  FAILED = 'FAILED',
}

/**
 * Validation constants - shared with backend patterns
 * DRY: Centralized validation rules
 */
export const BOOKING_VALIDATION = {
  /** Phone number pattern: digits, dash, plus, parentheses, spaces */
  PHONE_REGEX: /^[\d\-+()\s]+$/,
  /** US ZIP code pattern: 5 digits */
  ZIP_REGEX: /^\d{5}$/,
  /** 24-hour time format: HH:MM */
  TIME_REGEX: /^([01]\d|2[0-3]):([0-5]\d)$/,
  /** US state code length */
  STATE_CODE_LENGTH: 2,
  /** Field length limits */
  COMPANY_MAX_LENGTH: 100,
  CONTACT_NAME_MAX_LENGTH: 100,
  PHONE_MIN_LENGTH: 10,
  PHONE_MAX_LENGTH: 20,
  STREET_MAX_LENGTH: 200,
  CITY_MAX_LENGTH: 100,
  INSTRUCTIONS_MAX_LENGTH: 500,
  SPECIAL_INSTRUCTIONS_MAX_LENGTH: 1000,
} as const;

/**
 * Contact information for shipper/consignee
 * @see backend-tms/src/carrier-rates/dto/booking-request.dto.ts BookingContactDto
 */
export interface BookingContact {
  /** Company name (1-100 chars) */
  company: string;
  /** Contact person name (1-100 chars) */
  contactName: string;
  /** Phone number (10-20 chars, digits/dash/plus/parens/spaces) */
  phone: string;
  /** Email address */
  email: string;
  /** Phone extension (optional, 1-10 chars) */
  phoneExtension?: string;
}

/**
 * Address information for shipper/consignee
 * @see backend-tms/src/carrier-rates/dto/booking-request.dto.ts BookingAddressDto
 */
export interface BookingAddress {
  /** Street address line 1 (1-200 chars) */
  street1: string;
  /** Street address line 2 (optional, 1-200 chars) */
  street2?: string;
  /** City (1-100 chars) */
  city: string;
  /** State code (2-letter US state) */
  state: string;
  /** ZIP code (5-digit US) */
  zip: string;
  /** Country (optional, default: "US") */
  country?: string;
}

/**
 * Combined shipper/consignee information
 * @see backend-tms/src/carrier-rates/dto/booking-request.dto.ts ShipperConsigneeDto
 */
export interface ShipperConsigneeInfo {
  /** Contact details */
  contact: BookingContact;
  /** Address details */
  address: BookingAddress;
  /** Special instructions for this location (optional, max 500 chars) */
  instructions?: string;
}

/**
 * Pickup time window (HH:MM format, 24-hour)
 * @see backend-tms/src/carrier-rates/dto/booking-request.dto.ts PickupTimeWindowDto
 */
export interface PickupTimeWindow {
  /** Start time in HH:MM format (24hr) */
  startTime: string;
  /** End time in HH:MM format (24hr) */
  endTime: string;
}

/**
 * Create booking request payload
 * @see backend-tms/src/carrier-rates/dto/booking-request.dto.ts CreateBookingRequestDto
 */
export interface CreateBookingRequest {
  /** Selected response ID from the quote (UUID) */
  responseId: string;
  /** Shipper information */
  shipper: ShipperConsigneeInfo;
  /** Consignee information */
  consignee: ShipperConsigneeInfo;
  /** Pickup date (ISO date string YYYY-MM-DD) */
  pickupDate: string;
  /** Pickup time window (optional) */
  pickupTimeWindow?: PickupTimeWindow;
  /** Delivery instructions (optional, max 1000 chars) */
  deliveryInstructions?: string;
  /** Special shipment instructions (optional, max 1000 chars) */
  specialInstructions?: string;
}

/**
 * Create booking response
 * @see backend-tms/src/carrier-rates/dto/booking-response.dto.ts CreateBookingResponseDto
 */
export interface CreateBookingResponse {
  /** Success status */
  success: boolean;
  /** Created booking ID (UUID) */
  bookingId: string;
  /** Quote number (e.g., "RQ-20260113-ABC123") */
  quoteNumber: string;
  /** Bill of Lading number (e.g., "BOL-20260113-ABC123") */
  bolNumber: string;
  /** Booking status */
  status: BookingStatus;
  /** Carrier name */
  carrierName: string;
  /** Service level */
  serviceLevel: string;
  /** Total amount in dollars */
  totalAmount: number;
  /** Pickup date */
  pickupDate: string;
  /** Estimated delivery date (optional) */
  estimatedDeliveryDate?: string;
  /** Next steps message */
  message: string;
}

/**
 * Confirm booking request payload
 * @see backend-tms/src/carrier-rates/dto/booking-request.dto.ts ConfirmBookingRequestDto
 */
export interface ConfirmBookingRequest {
  /** Provider booking ID from carrier (optional) */
  providerBookingId?: string;
  /** PRO number - carrier tracking number (optional) */
  proNumber?: string;
  /** Estimated delivery date (optional) */
  estimatedDeliveryDate?: string;
}

/**
 * Confirm booking response
 * @see backend-tms/src/carrier-rates/dto/booking-response.dto.ts ConfirmBookingResponseDto
 */
export interface ConfirmBookingResponse {
  /** Success status */
  success: boolean;
  /** Booking ID (UUID) */
  bookingId: string;
  /** Updated booking status */
  status: BookingStatus;
  /** PRO number (optional) */
  proNumber?: string;
  /** Provider booking ID (optional) */
  providerBookingId?: string;
  /** Confirmation timestamp */
  confirmedAt: string;
  /** Confirmation message */
  message: string;
}

/**
 * Create shipment from booking response
 * @see backend-tms/src/carrier-rates/dto/booking-response.dto.ts CreateShipmentFromBookingResponseDto
 */
export interface CreateShipmentFromBookingResponse {
  /** Success status */
  success: boolean;
  /** Created shipment ID (UUID) */
  shipmentId: string;
  /** Shipment number (e.g., "SHP-20260113-ABC123") */
  shipmentNumber: string;
  /** Source booking ID (UUID) */
  bookingId: string;
  /** Number of cost entries created */
  costsCreated: number;
  /** Success message */
  message: string;
}
